import { type UseTrackAgentWorkspaceModifiedParams } from '@air/analytics';

import {
  didWorkspaceAgentMutationOccur,
  parseWorkspaceAgentMutationToolParams,
} from '~/components/v2/components/workspace/workspace-agent-panel/schemas/parseWorkspaceAgentMutationToolParams';
import { parseToolResultMetadata } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/toolResultMetadata';
import { type WorkspaceAgentMessage } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentMessage';
import { buildWorkspaceAgentToolCallById } from '~/components/v2/components/workspace/workspace-agent-panel/utils/buildWorkspaceAgentToolCallById';
import { enrichWorkspaceAgentMutationToolParams } from '~/components/v2/components/workspace/workspace-agent-panel/utils/enrichWorkspaceAgentMutationToolParams';
import { resolveWorkspaceAgentMutatingToolName } from '~/components/v2/components/workspace/workspace-agent-panel/utils/resolveWorkspaceAgentMutatingToolName';

export type ReadWorkspaceAgentMutationAnalyticsFromToolResultParams = {
  messages: readonly WorkspaceAgentMessage[];
  toolResultMessage: WorkspaceAgentMessage;
};

/** Reads analytics payload for a mutating toolResult. Fires only when mutation_occurred would be true. */
export const readWorkspaceAgentMutationAnalyticsFromToolResult = ({
  messages,
  toolResultMessage,
}: ReadWorkspaceAgentMutationAnalyticsFromToolResultParams): UseTrackAgentWorkspaceModifiedParams | null => {
  if (toolResultMessage.role !== 'toolResult') {
    return null;
  }

  const metadataResult = parseToolResultMetadata(toolResultMessage.metadata);

  if (metadataResult?.outcome !== 'readable') {
    return null;
  }

  const toolCallById = buildWorkspaceAgentToolCallById(messages);
  const toolName = resolveWorkspaceAgentMutatingToolName(toolResultMessage, toolCallById);

  if (!toolName) {
    return null;
  }

  const success = metadataResult.metadata.success === true;
  const parsedToolParams = parseWorkspaceAgentMutationToolParams(toolName, toolResultMessage.content);
  const toolParams = enrichWorkspaceAgentMutationToolParams({
    toolCall: toolCallById.get(metadataResult.metadata.toolCallId),
    toolName,
    toolParams: parsedToolParams ?? {},
  });
  const mutationOccurred = didWorkspaceAgentMutationOccur(toolName, success, parsedToolParams);

  if (!mutationOccurred) {
    return null;
  }

  return {
    tool_name: toolName,
    success,
    mutation_occurred: mutationOccurred,
    tool_params: toolParams,
  };
};
